import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    description: '',
    images: '',
    year: new Date().getFullYear(),
    client: '',
    type: 'real',
    featured: false,
  });

  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?type=project`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        ...project,
        images: project.images.join('\n'),
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        slug: '',
        category: '',
        description: '',
        images: '',
        year: new Date().getFullYear(),
        client: '',
        type: 'real',
        featured: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    const projectData = {
      ...formData,
      images: formData.images.split('\n').filter((url) => url.trim()),
      year: parseInt(formData.year),
    };

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.slug}`, projectData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Proyecto actualizado');
      } else {
        await axios.post(`${API}/projects`, projectData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Proyecto creado');
      }
      fetchProjects();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar proyecto');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('¿Estás seguro de eliminar este proyecto?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/projects/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Proyecto eliminado');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Error al eliminar proyecto');
    }
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="projects-title">
            Proyectos
          </h1>
          <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Gestionar Portfolio</p>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="add-project-button" className="bg-ink text-paper hover:bg-editorial">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proyecto
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <p className="text-lg font-light text-ink/60">Cargando proyectos...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-paper p-12 editorial-border text-center">
          <p className="text-lg font-light text-ink/60 mb-4">No hay proyectos creados</p>
          <Button onClick={() => handleOpenModal()} className="bg-ink text-paper hover:bg-editorial">
            Crear Primer Proyecto
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-paper editorial-border overflow-hidden group"
              data-testid="project-card"
            >
              <div className="h-48 overflow-hidden">
                <img src={project.images[0]} alt={project.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-sans tracking-widest uppercase text-editorial">{project.category}</span>
                  {project.type === 'ficticio' && (
                    <span className="text-xs font-sans tracking-widest uppercase text-ink/50">CONCEPTO</span>
                  )}
                </div>
                <h3 className="text-lg font-bold tracking-tight mb-2">{project.title}</h3>
                <p className="text-sm text-ink/60 mb-4">{project.year}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleOpenModal(project)}
                    data-testid="edit-project-button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(project.slug)}
                    data-testid="delete-project-button"
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-6" data-testid="project-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-paper p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto editorial-border"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold tracking-tight">
                {editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
              </h2>
              <button onClick={handleCloseModal} className="hover:text-editorial transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    data-testid="project-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    data-testid="project-slug-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Categoría</Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    data-testid="project-category-input"
                  />
                </div>
                <div>
                  <Label htmlFor="year">Año</Label>
                  <Input
                    id="year"
                    name="year"
                    type="number"
                    value={formData.year}
                    onChange={handleChange}
                    required
                    data-testid="project-year-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Tipo</Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-ink/20 focus:border-editorial focus:outline-none"
                    data-testid="project-type-select"
                  >
                    <option value="real">Real</option>
                    <option value="ficticio">Ficticio</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="client">Cliente (opcional)</Label>
                  <Input
                    id="client"
                    name="client"
                    value={formData.client}
                    onChange={handleChange}
                    data-testid="project-client-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="project-description-input"
                />
              </div>

              <div>
                <Label htmlFor="images">Imágenes (una URL por línea)</Label>
                <Textarea
                  id="images"
                  name="images"
                  value={formData.images}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="https://example.com/image1.jpg"
                  data-testid="project-images-input"
                />
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="flex-1 bg-ink text-paper hover:bg-editorial" data-testid="save-project-button">
                  {editingProject ? 'Actualizar' : 'Crear'} Proyecto
                </Button>
                <Button type="button" onClick={handleCloseModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;